import dayjs from 'dayjs';
import { releaseCloseConfirm, suppressCloseConfirm } from 'hooks/closeConfirm';
import { isEmpty } from 'utils/stringUtil';

// EC購入導線（店舗在庫引当）固有パラメータの名前空間（ec 接頭辞）
// 例: ecOrderNo / ecMeisaiNo / ecResvStartYmd / ecChubunCd / ecShobunCd / ecTokCd / ecCarNo / ecYykNo
export const EC_PREFIX = 'ec';

// EC購入導線分岐で許可する ec* 固有パラメータ（7項目）
export const EC_ONLY_PARAMS = [
  'ecOrderNo', // EC注文番号
  'ecMeisaiNo', // EC明細番号（任意）
  'ecResvStartYmd', // 予約可能開始日（任意）
  'ecChubunCd', // 中分類
  'ecShobunCd', // 小分類
  'ecTokCd', // 店舗コード
  'ecCarNo', // 車両連番（任意）
];

// EC戻り遷移（フォームPOST）の宛先URL。EC側命名に合わせるため ec 接頭辞は付けない
// （EC_ONLY_PARAMS は ec 接頭辞の名前空間を表す配列のため、ここには含めない）
export const EC_CALLBACK_URL_PARAM = 'callbackUrl';

// EC動線タイムアウトモーダル（ecTimeoutModal）の表示までの経過時間（1時間）。
// EC動線開始時刻（ecFlow.startedAt）からの絶対経過で判定し、無操作リセットはしない
export const EC_TIMEOUT_MS = 60 * 60 * 1000;

// EC購入導線の初回予約で必須となる項目
// （ecResvStartYmd/ecCarNo/ecMeisaiNoは任意のため対象外）
export const EC_REQUIRED_PARAMS = [
  'ecOrderNo',
  'ecChubunCd',
  'ecShobunCd',
  'ecTokCd',
];

// EC購入導線の日時変更リクエストで必須となる3項目。
// ecChubunCd/ecShobunCd/ecTokCd/ecCarNo/ecMeisaiNoは任意
export const EC_MODIFY_REQUIRED_PARAMS = [
  'ecYykNo',
  'ecOrderNo',
  'ecResvStartYmd',
];

// EC購入導線分岐の許可リスト（従来 p* ＋ ec* を併記。ec*/p* は排他）
export const EC_USE_PARAMS = [
  'p1',
  'p2',
  'p3',
  'p4',
  'p5',
  'p6',
  'p7',
  'p8',
  'p9',
  'p10',
  'p11',
  'p12',
  'p13',
  ...EC_ONLY_PARAMS,
  EC_CALLBACK_URL_PARAM,
];

/**
 * EC固有の日付パラメータ（ecResvStartYmd）の書式・実在日を検証する。
 * 書式は YYYYMMDD（8桁・実在日）。無指定（空/undefined）は許容する。
 */
export const isValidResvYmd = v => !v || dayjs(v, 'YYYYMMDD', true).isValid();

/**
 * EC戻り遷移の宛先パラメータ（callbackUrl）の書式・ドメインを検証する。
 * 無指定（空/undefined/null）は許容する（env の固定URLへフォールバックするため）。
 * 指定時は https のオートバックスドメイン（autobacs.com またはそのサブドメイン）のみ許可する。
 */
export const isValidCallbackUrl = v => {
  if (!v) return true;
  let url;
  try {
    url = new URL(v);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  return (
    url.hostname === 'autobacs.com' || url.hostname.endsWith('.autobacs.com')
  );
};

/**
 * EC注文番号（order_id）の接頭辞 'EC_' を1回だけ取り除く。
 * 'EC_' で始まらない値・空文字・null/undefined・文字列以外はそのまま返す（型変換はしない）。
 */
export const trimEcOrderNoPrefix = v =>
  typeof v === 'string' && v.startsWith('EC_') ? v.slice('EC_'.length) : v;

/**
 * EC購入導線の初回予約に必須の4項目（ecOrderNo/ecChubunCd/ecShobunCd/ecTokCd）がすべて指定されているか判定する。
 */
export const hasRequiredEcParams = ({
  ecOrderNo,
  ecChubunCd,
  ecShobunCd,
  ecTokCd,
}) => ![ecOrderNo, ecChubunCd, ecShobunCd, ecTokCd].some(isEmpty);

/**
 * EC動線（初回予約・日時変更共通）の確定成功後、C4（EC注文画面）へ戻るリンク。
 * 宛先は callbackUrl を優先し、無指定・不正時のみ env の固定URL（REACT_APP_EC_RENKEI_URL）へフォールバックする。
 */
export const transitionToEcOrderScreen = (reservationInfo, callbackUrl) => {
  const form = document.createElement('form');
  form.method = 'POST';
  // callbackUrl（EC側から渡された戻り先）が有効な場合はそれを優先し、
  // 未指定・不正な場合は従来どおり env の固定URLへフォールバックする（§11.2）
  form.action =
    isValidCallbackUrl(callbackUrl) && callbackUrl
      ? callbackUrl
      : process.env.REACT_APP_EC_RENKEI_URL;

  const appendHiddenInput = (name, value) => {
    if (value === undefined || value === null) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    form.appendChild(input);
  };

  Object.entries({
    ...reservationInfo,
    // 暫定で「1:受付」を設定する
    status: '1',
  }).forEach(([key, value]) => {
    appendHiddenInput(key, value);
  });

  document.body.appendChild(form);
  // form送信によるC4画面への遷移はユーザー操作に基づく意図的な離脱のため、
  // beforeunloadの離脱確認ダイアログを抑止する
  suppressCloseConfirm();
  try {
    form.submit();
  } catch (e) {
    // 同期例外等でPOST遷移が成立しなかった場合、抑止フラグが残ったままにならないよう解除してから再スローする
    releaseCloseConfirm();
    throw e;
  }
};
